/**
 * Japanese message catalog.
 *
 * Typed as Record<MessageKey, string>, so it defines every key in en.ts.
 */
import type { MessageKey } from './en';

export const ja: Record<MessageKey, string> = {
  // Common
  'common.unknown': '不明',
  'common.tryAgain': 'もう一度試す',
  'common.walletFallback': 'ウォレット',
  'common.loadingWallets': 'ウォレットを読み込んでいます...',
  'common.back': '戻る',
  'common.close': '閉じる',
  'common.loading': '読み込み中…',
  'common.select': '選択',
  'common.selected': '選択済み',
  'common.beta': 'BETA',
  'common.cancel': 'キャンセル',
  'common.refresh': '更新',
  'common.refreshing': '更新中',
  'common.all': 'すべて',
  'common.wallets': 'ウォレット',
  'common.wallet': 'ウォレット',
  'common.networks': 'ネットワーク',
  'common.network': 'ネットワーク',
  'common.pending': '保留中',
  'common.unavailable': '利用不可',
  'common.unverified': '未検証',
  'common.settings': '設定',
  'menu.title': 'メニュー',
  'menu.open': 'メニューを開く',
  'menu.close': 'メニューを閉じる',
  'common.tier.testnet': 'テストネット',
  'common.tier.mainnet': 'メインネット',
  'common.theme.auto': '自動',
  'common.theme.light': 'ライト',
  'common.theme.dark': 'ダーク',

  // Navigation
  'nav.primary': 'メイン',
  'nav.home': 'ホーム',

  // Settings — page
  'settings.title': '設定',

  // Settings — approvals section
  'settings.approvals.title': '承認',
  'settings.approvals.hint': 'ゲートウェイが署名する前に、判断待ちのトランザクションを確認します。',
  'settings.approvals.link': '承認待ちを確認',

  // Settings — appearance section
  'settings.appearance.title': '外観',
  'settings.appearance.hint': 'アプリの表示方法を選択します。',
  'settings.appearance.themeGroup': 'テーマ',

  // Settings — language section
  'preauth.menu.title': '言語と外観',
  'preauth.menu.open': '言語と外観メニューを開く',
  'settings.language.title': '言語',
  'settings.language.hint': '使用する言語を選択します。',

  // Settings — developer mode section
  'settings.devmode.title': '開発者モード',
  'settings.devmode.label': '開発者モード',
  'settings.devmode.desc': 'オンではテストネット、オフではメインネットを使用します。',
  'settings.devmode.note': 'テストネットモードです。残高に実際の価値はありません。',

  // Settings — deployment section
  'settings.deployment.title': 'デプロイ情報',
  'settings.deployment.network': 'ネットワーク',
  'settings.deployment.gatewayEnv': 'ゲートウェイ環境',
  'settings.deployment.bitcoinNetwork': 'Bitcoin ネットワーク',
  'settings.deployment.organization': '組織',

  // Settings — security section (device passkeys)
  'settings.security.title': 'セキュリティ',
  'settings.security.hint': 'ゲートウェイが署名する前にこのデバイスで取引を承認できるよう、デバイスパスキーを追加します。',
  'settings.security.methodsTitle': '認証方法',
  'settings.security.addDevice': '認証方法を追加',
  'settings.security.limitReached': '認証方法は最大{max}件まで登録できます。',
  'settings.security.adding': 'デバイスを待機中…',
  'settings.security.remove': '削除',
  'settings.security.removing': '削除中…',
  'settings.security.empty': '登録済みのデバイスパスキーはありません。',
  'settings.security.loading': '認証方法を読み込んでいます…',
  'settings.security.retry': 'もう一度試す',
  'settings.security.unsupported': 'このブラウザはデバイスパスキーに対応していません。',
  'settings.security.enrolledOn': '{date}に追加',
  'settings.security.lastUsed': '最終使用：{date}',
  'settings.security.deviceFallbackLabel': 'デバイスパスキー',

  // Create-a-passkey prompt (one-time, after a fresh OAuth or OTP login)
  'passkey.create.title': 'パスキーを作成',
  'passkey.create.body': '指紋、顔認証、画面ロックでログインできます。パスワードを覚える必要はありません。',
  'passkey.create.cta': 'パスキーを作成',
  'passkey.create.creating': 'デバイスを待機中…',
  'passkey.create.dismiss': '今後表示しない',
  'passkey.create.error': 'パスキーを作成できませんでした。もう一度試すか、後でメニューのセキュリティから追加してください。',
  'passkey.stateUnknown':
    '送金は行われませんでした。接続の問題で認証方法を確認できなかったため、もう一度お試しください。',

  // Settings — account section
  'settings.account.title': 'アカウント',
  'settings.account.signout': 'ログアウト',

  // Login — page
  'login.title': 'ウォレットに進む',
  'login.eyebrow': 'ログイン',
  'login.error.start': 'ログインを開始できませんでした。接続を確認してもう一度お試しください。',
  'login.divider': 'または',
  'login.more': 'その他のログイン方法',

  // Login — providers ({name} is the brand for the fallback label)
  'login.provider.google': 'Google で続ける',
  'login.provider.apple': 'Apple で続ける',
  'login.provider.discord': 'Discord で続ける',
  'login.provider.twitter': 'X で続ける',
  'login.provider.kakao': 'Kakao で続ける',
  'login.provider.fallback': '{name} で続ける',
  'login.provider.unavailable': 'このログイン方法は現在利用できません。',

  // Login — passkey sign-in
  'login.passkey.button': '安全なログイン',
  'login.passkey.desc': '顔認証や指紋認証など、お使いのデバイスに登録された認証方法を使用します。',
  'login.passkey.pending': 'パスキーを待機中…',
  'login.passkey.cancelled':
    'パスキーでのログインがキャンセルされたか、このデバイスにはこのサイト用のパスキーがありません。もう一度試すか、別の方法を選択してください。',
  'login.passkey.error': 'パスキーでログインできませんでした。もう一度試すか、別の方法を選択してください。',
  // Shown when sign-in found no passkey or no account. Passkey creation lives in the
  // low-weight "create an account with a passkey" link below, so this points there.
  'login.passkey.noAccount': 'このデバイスにパスキーが見つかりません。下の「ウォレットを作成」から作成してください。',

  // Login — passkey signup (a low-weight link below sign-in for a new user)
  'login.signup.prompt': '初めてですか？',
  'login.signup.link': 'ウォレットを作成',
  'login.signup.pending': 'アカウントを作成中…',
  'login.signup.exists': '既にアカウントがあります。パスキーでログインしてください。',
  'login.signup.error': 'アカウントを作成できませんでした。もう一度試すか、別の方法を選択してください。',

  // Login — organization escape hatch (inside the "more ways to sign in" section)
  'login.org.label': '組織 ID',
  'login.org.note': '自分のスペースにログインする場合は空欄にし、上からログイン方法を選択してください。',

  // Login — developer fallback
  'login.dev.body': 'ローカル開発でゲートウェイに接続する場合は、ブラウザコンソールからトークンを設定してください：',

  // Dashboard (home)
  'dashboard.offline': 'オフラインのようです。再接続してウォレットを読み込んでください。',
  'dashboard.empty': 'ウォレットがまだありません。作成して始めましょう。',

  // Home components
  'home.account.allWallets': 'すべてのウォレット',
  'home.account.subtitle': '{wallets} {walletWord} · {networks} {networkWord}',
  'home.switcher.aria': 'アカウントを切り替え、現在は {name}',
  'home.copy.aria': '{name} のアドレスをコピー',
  'home.portfolio.aria': 'ポートフォリオ残高',
  'home.portfolio.total': '合計残高',
  'home.actions.aria': 'ウォレット操作',
  'home.action.swapUnavailable': 'スワップはまだ利用できません。',
  'home.action.buySellUnavailable': '売買はまだ利用できません。',
  'home.action.buySell': '購入/売却',
  'home.network.testnetTitle': 'テストネット環境です。表示される残高に実際の価値はありません。',
  'home.network.mainnetTitle': 'メインネットです。残高は稼働中のネットワークを反映します。',

  // App navigation / account-wide surfaces
  'activity.title': 'アクティビティ',
  'activity.history': '履歴',

  // Transaction list
  'tx.aria.history': '取引履歴',
  'tx.date.pending': '保留中',
  'tx.direction.receive': '受取',
  'tx.direction.send': '送信',
  'tx.direction.swap': 'スワップ',
  'tx.relation.from': '送信元',
  'tx.relation.to': '送信先',
  'tx.swapDetails.aria': 'スワップ詳細：{from} から {to}',
  'tx.status.pending': '保留中',
  'tx.status.pending_approval': '承認待ち',
  'tx.status.pending_policy': 'ポリシー確認中',
  'tx.status.signing': '署名中',
  'tx.status.signed': '署名済み',
  'tx.status.submitted': '送信済み',
  'tx.status.broadcast': 'ブロードキャスト済み',
  'tx.status.confirmed': '確定済み',
  'tx.status.failed': '失敗',
  'tx.status.rejected': '拒否済み',
  'tx.status.unknown': '不明',
  'tx.loading': 'アクティビティを読み込み中…',
  'tx.empty': 'アクティビティはまだありません。',
  'tx.empty.hint': '送信とスワップの履歴がここに表示されます。',
  'tx.loadingMore': 'さらに読み込み中…',
  'tx.explorer.aria': 'ブロックエクスプローラーで表示',

  // Approvals
  'approvals.title': '承認',
  'approvals.error.load': '承認を読み込めませんでした',
  'approvals.empty.title': 'すべて完了しました',
  'approvals.empty.body': '判断待ちのトランザクションはありません。確認が必要な新しいリクエストはここに表示されます。',
  'approvals.wallet.title': 'このウォレットの承認',
  'approvals.wallet.resolving': 'このウォレットに属する承認を確認中…',
  'approvals.wallet.empty.title': 'このウォレットの承認待ちはありません',
  'approvals.wallet.empty.body': 'このウォレットに判断待ちのトランザクションはありません。確認が必要なリクエストはここに表示されます。',
  'approvals.status.pending': '保留中',
  'approvals.status.approved': '承認済み',
  'approvals.status.rejected': '拒否済み',
  'approvals.status.expired': '期限切れ',
  'approvals.quorum': '{required} 件中 {received} 件承認済み',
  'approvals.quorum.aria': 'クォーラムの進捗',
  'approvals.waiting.more': 'あと {count} 件の{word}を待っています',
  'approvals.waiting.approval': '承認',
  'approvals.waiting.approvals': '承認',
  'approvals.waiting.signing': '署名セレモニーの完了を待っています',
  'approvals.timeline.checker': '確認者',
  'approvals.timeline.acted': 'が対応',
  'approvals.detail.loading': '正確なトランザクションを読み込み中…',
  'approvals.detail.unavailable': '取引詳細を読み込めませんでした。別の方法でこの送金を確認できる場合に限り、承認または拒否してください。',
  'approvals.detail.notProvided': '取引記録に情報がありません',
  'approvals.detail.requestedBy': 'リクエスト元',
  'approvals.detail.transactionStatus': '取引ステータス',
  'approvals.summary.transaction': 'トランザクション',
  'approvals.action.busy': '複数者署名を調整中です。クォーラムに達すると、ゲートウェイが署名・ブロードキャストしてから応答するため、数秒かかる場合があります。',
  'approvals.action.approve': '承認',
  'approvals.action.reject': '拒否',
  'approvals.confirm.approveTitle': 'このトランザクションを承認しますか？',
  'approvals.confirm.rejectTitle': 'このトランザクションを拒否しますか？',
  'approvals.confirm.approveConsequence':
    '承認がゲートウェイに送信されます。クォーラムに達すると、ゲートウェイがこのトランザクションに署名・ブロードキャストしてから応答するため、数秒かかる場合があります。完了するまでアプリを閉じないでください。',
  'approvals.confirm.rejectConsequence': 'トランザクションの署名を中止します。クォーラムに到達できなくなるため、ブロードキャストされません。',
  'approvals.confirm.reasonLabel': '理由（任意）',
  'approvals.confirm.reasonPlaceholder': '監査記録用のメモを追加',
  'approvals.confirm.approving': '承認中',
  'approvals.confirm.rejecting': '拒否中',
  'approvals.section.pending': '確認が必要（{count}）',
  'approvals.section.decided': '対応済み',

  // Create wallet
  'create.title': 'ウォレットを作成',
  'create.lead': 'ウォレットの種類と名前を選択してください。鍵生成はサーバー上で安全に実行され、デバイスに鍵を保存したり、ここで署名したりすることはありません。',
  'create.walletName': 'ウォレット名',
  'create.walletPlaceholder': 'マイウォレット',
  'create.walletType': 'ウォレットの種類',
  'create.kind.evm.label': 'EVM アカウント',
  'create.kind.evm.desc': 'Ethereum · Base · BSC · HyperEVM',
  'create.kind.tron.label': 'Tron',
  'create.kind.tron.desc': 'TRX と TRC-20 トークン',
  'create.kind.solana.label': 'Solana',
  'create.kind.solana.desc': 'SOL と SPL トークン',
  'create.kind.sui.label': 'Sui',
  'create.kind.sui.desc': 'SUI と Sui トークン',
  'create.kind.bitcoin.label': 'Bitcoin',
  'create.kind.bitcoin.desc': 'BTC · SegWit アドレス',

  'provisioning.title': 'ウォレットを作成しています',
  'provisioning.unavailable': 'ここでは利用できません',
  'walletSettings.statusFrozen': '凍結中',
  'dashboard.stillCreating': '残り{count}個のウォレットを作成中です。',
  'dashboard.createWallet': 'ウォレットを作成',
  'provisioning.failed': 'ウォレットを自動で作成できませんでした。',
  'provisioning.createManually': '手動でウォレットを作成',
  'provisioning.succeeded': 'ウォレットの準備ができました。',
  'provisioning.partial':
    '{created}のウォレットは正常に作成されましたが、{missing}のウォレットは作成できませんでした。',
  'provisioning.partialHint': '作成できなかったウォレットは、後から手動で追加できます。',
  'provisioning.pending': 'ウォレットはまだ作成中です。',
  'provisioning.pendingHint': 'まもなく、ウォレットはホーム画面に自動的に表示されます。',
  'provisioning.goHome': '続ける',
  'create.busy': '鍵生成セレモニーを実行中',
  'create.toast.created': 'ウォレットを作成しました。',

  // OAuth callback
  'oauth.pending.title': 'ログイン中',
  'oauth.pending.message': 'ログインを完了しています。少々お待ちください。',
  'oauth.failure.title': 'ログインを完了できませんでした',
  'oauth.failure.fallback': 'ログインを完了できませんでした。',
  'oauth.backToSignIn': 'ログインに戻る',

  // Receive ({chain} is the resolved chain name)
  'receive.title': '受取',
  'receive.subtitle': '受け取るネットワークとアドレスを選択',
  'receive.chain.fallback': 'このウォレット',
  'receive.chainSelector.aria': '受け取るネットワークを選択',
  'receive.addressPicker.label': 'アドレス',
  'receive.addressPending': 'アドレス準備中',
  'receive.qr.title': '{chain} アドレスの QR コード',
  'receive.qr.unavailable': 'QR コードを利用できません。下のアドレスをコピーしてください。',
  'receive.addressLabel': '{chain} アドレス',
  'receive.warning': 'このアドレスには {chain} の資産のみ送信してください。',
  'receive.copy.copy': 'アドレスをコピー',
  'receive.copy.copied': 'アドレスをコピーしました',
  'receive.copy.failed': 'コピーに失敗しました',
  'receive.copy.aria': 'アドレスをクリップボードにコピー',
  'receive.pending.setup': 'このウォレットはまだセットアップ中です。準備ができるとアドレスが表示されます。',
  'receive.pending.loading': 'ウォレットのアドレスを読み込み中...',
  'receive.empty': '受取に使えるウォレットがまだありません。',

  // Send ({asset}, {chain}, {label}, {reason} are interpolated at render)
  'send.title': '送信',
  'send.selectAsset': '送信する資産を選択',
  'send.loadingAssets': '資産を読み込んでいます...',
  'send.emptyAssets': '送信可能な残高のある資産がありません。まず資産を受け取ってください。',
  'send.emptyNoWallets': 'ウォレットがまだありません。送信元のウォレットを作成してください。',
  'send.backToAssets': '資産に戻る',
  'send.stepTwo.title': '{chain} ネットワークで {asset} を送信',
  'send.context.none': 'ウォレットが選択されていません。',
  'send.context.from': '{chain} の {label} から',
  'send.context.fromUnsupported': '{label} から。{reason}',
  'send.error.wrongTier': 'このウォレットは現在のネットワーク上にありません。ネットワークを切り替えるか、現在のネットワーク上のウォレットを選択してください。',
  'send.error.noWallet': '送信元のウォレットを選択してください。',
  'send.error.chainUnavailable': 'このネットワークでは送信できません。',
  'send.status.submitting': 'トランザクションを送信中です。',
  'send.status.buildingPayload': 'ネットワーク手数料とノンスを取得しています。',
  'send.status.pendingPolicy': 'この送金には追加承認が必要か、ポリシーによるタイムロックが設定されています。条件を満たすまで処理されません。',
  'send.status.noApproval': 'トランザクションは作成されましたが、ここでは承認操作ができません。承認画面で確認して続行してください。',
  'send.confirm.title': '送信を確認',
  'send.confirm.note': '送信先アドレスをよく確認してください。資金の移動は取り消せません。',
  'send.confirm.to': '送信先',
  'send.confirm.amount': '金額',
  'send.confirm.network': 'ネットワーク',
  'send.confirm.networkFee': 'ネットワーク手数料',
  'send.confirm.networkFeeValue': 'ゲートウェイがトランザクションを作成する際に見積もり、適用します。',
  'send.confirm.networkFeeValueEvm': '確認時にネットワークから取得し、取引に適用されます。',
  'send.confirm.submit': '確認して送信',
  'send.confirm.sending': '送信中',
  'send.status.title': '送信ステータス',
  'send.status.statusLabel': 'ステータス',
  'send.status.txId': 'トランザクション ID',
  'send.status.hash': 'ハッシュ',
  'send.status.explorer': 'ブロックエクスプローラーで表示',
  'send.status.goToApprovals': '承認へ移動',
  'send.status.backToSend': '送信に戻る',
  'send.result.status': '送金{status}。',
  'send.result.statusReason': '送金{status}{reason}',

  // Send form and asset picker
  'send.form.recipient': '受取人アドレス',
  'send.form.destinationPlaceholder': '送信先アドレス',
  'send.form.noWallet': '送信元のウォレットを選択してください。',
  'send.form.chainUnavailable': 'このネットワークではここから送信できません。',
  'send.validation.recipientRequired': '受取人アドレスを入力してください。',
  'send.validation.evmWebUnavailable': 'このネットワークは Web アプリからの送信に対応していません。',
  'send.validation.evmNoNetwork': '送信するコインを選ぶとネットワークが決まります。このアカウントはすべての EVM ネットワークで同じアドレスを使用します。',
  'send.validation.evmTokenUnsupported': 'EVM ネットワークでのトークン送信は Web アプリではまだ利用できません。トークンはモバイルアプリをご利用いただくか、ここではそのネットワークの基軸コインを送信してください。',
  'send.validation.evmImportedTokenUnsupported': 'インポートしたトークンはウェブアプリから送信できません。ここではレジストリで検証済みのトークンとネットワーク自体のコインのみ送信できます。',
  'send.validation.evmTokenDecimalsUnknown': 'このトークンの小数精度を確認できないため、送金を安全にエンコードできません。何も署名されていません。',
  'send.validation.evmPrefix': 'EVM アドレスは 0x で始まります。',
  'send.validation.evmFormat': 'EVM アドレスは 0x に続く 16 進数 40 文字です。',
  'send.validation.evmChecksum': 'この EVM アドレスはチェックサムが正しくありません。',
  'send.validation.evmZeroAddress': 'ゼロアドレスです。ここへ送った資産は取り戻せません。',
  'send.validation.suiPrefix': 'Sui アドレスは 0x で始まります。',
  'send.validation.suiFormat': 'Sui アドレスは 0x に続く64文字の小文字16進数です。',
  'send.validation.tronPrefix': 'Tron アドレスは T で始まります。',
  'send.validation.tronLength': 'Tron アドレスは34文字です。',
  'send.validation.tronChecksum': 'この Tron アドレスのチェックサムは無効です。',
  'send.validation.tronInvalid': '有効な Tron アドレスではありません。',
  'send.validation.solanaInvalid': '有効な Solana アドレスではないようです。',
  'send.validation.solanaBytes': 'Solana アドレスはデコードすると32バイトになります。',
  'send.validation.bitcoinChecksum': 'この Bitcoin アドレスのチェックサムは無効です。',
  'send.validation.bitcoinInvalid': '有効な Bitcoin アドレスではありません。',
  'send.validation.bitcoinSegwitInvalid': '有効な SegWit アドレスではありません。',
  'send.validation.bitcoinTaprootInvalid': '有効な Taproot アドレスではありません。',
  'send.validation.bitcoinWrongNetwork': 'このアドレスは別の Bitcoin ネットワーク用です（想定：{expected}）。',
  'send.validation.bitcoinDust': '金額が Bitcoin のダスト下限 {sats} sats を下回っています。',
  'send.form.amountRequired': '金額を入力してください。',
  'send.form.numberRequired': '数値を入力してください。',
  'send.form.amountPositive': '金額は0より大きい必要があります。',
  'send.form.decimals': '{symbol} は小数点以下 {decimals} 桁まで対応しています。',
  'send.form.exceedsBalance': '金額が {symbol} の残高を超えています。',
  'send.form.assetAria': '資産',
  'send.form.noAssets': '資産なし',
  'send.form.available': '利用可能：{amount} {symbol}',
  'send.form.review': '送信内容を確認',
  'send.form.clear': 'クリア',
  'send.assets.searchLabel': '資産を検索',
  'send.assets.searchPlaceholder': '名前またはシンボルで検索',
  'send.assets.empty': '一致する資産がありません。検索またはフィルターをクリアすると保有資産を確認できます。',

  // Swap ({decimals}, {provider}, {count} are interpolated at render)
  'swap.title': 'スワップ',
  'swap.subtitle': 'すべてのプロバイダーから最適なルートを検索',
  'swap.balance': '残高 {amount} {symbol}',
  'swap.max': '最大',
  'swap.signer.overridden': '{selected} はスワップできないため、{label} · {address} を使用します。',
  'swap.empty.evm': 'スワップには EVM アドレスを持つウォレットが必要です。まずホーム画面から作成してください。',
  'swap.error.loadChains': 'ネットワークを読み込めませんでした',
  'swap.error.quote': '見積もりに失敗しました',
  'swap.error.executeNotEnabled': 'このサービスではスワップの実行が有効になっていません（開発用署名機能はオフです）。',
  'swap.error.executeFallback': 'スワップに失敗しました',
  'swap.side.from': '支払う',
  'swap.side.to': '受け取る',
  'swap.chain.fromAria': '送信元ネットワーク',
  'swap.chain.toAria': '送信先ネットワーク',
  'swap.amount.fromAria': '送信する金額',
  'swap.amount.toAria': '受け取る金額',
  'swap.amount.invalidHint': '小数点以下 {decimals} 桁以内の正の金額を入力してください。',
  'swap.flip.aria': 'スワップ方向を切り替え',
  'swap.sameTokenHint': '異なる2つのトークンを選択してください。',
  'swap.warning.insufficientBalance': '残高がこの金額を下回っているため、スワップは失敗します。',
  'swap.warning.insufficientAllowance': 'このスワップの前にトークンの承認が必要です。',
  'swap.toast.submitted': '{provider} 経由でスワップを送信しました。アクティビティで追跡できます。',
  'swap.confirm.swapping': 'スワップ中…',
  'swap.confirm.via': '{provider} でスワップ',
  'swap.confirm.summary': '{provider}経由で{inAmount} {inSymbol}を約{outAmount} {outSymbol}にスワップします。決済は宛先ネットワーク上で非同期に完了します。',
  'swap.mpcNote': 'MPC署名フローを通じてウォレットが署名します。元のトランザクションはオンチェーンでブロードキャストされ、プロバイダーが約定を決済します。',
  'swap.confirm.default': 'スワップ',
  'swap.confirm.disabledTitle': 'スワップ実行はゲートウェイ署名連携とともに提供予定です',
  'swap.mock.enabledHint': '開発用ウォレット（モック MPC）で署名 · 実際のオンチェーンスワップ',
  'swap.mock.disabledHint': 'リアルタイム見積もり · スワップ実行はゲートウェイ署名フローとともに提供予定',
  'swap.picker.tokenAria': 'トークンを選択',
  'swap.picker.chainAria': 'ネットワークを選択',
  'swap.picker.searchToken': '名前、シンボル、アドレスを検索',
  'swap.picker.searchChain': 'ウォレットを検索',
  'swap.picker.loadingTokens': 'トークンを読み込み中…',
  'swap.picker.noTokens': '一致するトークンがありません。',
  'swap.picker.noChains': '一致するネットワークがありません。',
  'swap.picker.noWallets': 'ルート可能なチェーンで受け取れるウォレットがありません。',
  'swap.picker.routes': '{count} 件のルート',
  'swap.token.aria': 'トークンを選択',
  'swap.quote.noRoute': 'ルートなし',
  'swap.quote.best': '最適',
  'swap.quote.networkCost': 'ネットワーク手数料',
  'swap.quote.platformFee': 'プラットフォーム手数料',
  'swap.quote.priceImpact': '価格への影響',
  'swap.quote.time': '所要時間',

  // Swap detail sheet
  'swap.detail.aria': 'スワップ詳細',
  'swap.detail.closeAria': '詳細を閉じる',
  'swap.detail.status': 'ステータス',
  'swap.detail.paid': '支払額',
  'swap.detail.received': '受取額',
  'swap.detail.rate': 'レート',
  'swap.detail.route': 'ルート',
  'swap.detail.provider': 'プロバイダー',
  'swap.detail.feeQuoted': '手数料（見積もり）',
  'swap.detail.feeCharged': '手数料（請求額）',
  'swap.detail.sourceTx': '送信元トランザクション',
  'swap.detail.destinationTx': '送信先トランザクション',
  'swap.origin.unreported': 'トランザクションを送信中です。結果はまだ不明です。',
  'swap.origin.reported': 'ネットワーク上で該当するトランザクションを確認しています。',
  'swap.origin.verified': '送信元チェーンでの送信を確認しました。送信先チェーンでの処理を待っています。',
  'swap.origin.reverted': 'トランザクションは実行されましたが、失敗しました。資金は移動していません。',
  'swap.origin.unknown':
    'このトランザクションを確認できませんでした。再試行する前にウォレットの履歴を確認してください。',
  'swap.origin.noRetry':
    'このトランザクションが確認されるか、存在しないと判断されるまで再試行できません。',
  'swap.origin.ackPrompt':
    'このスワップは未確認です。解除する前にウォレットの履歴を確認してください。',
  'swap.origin.ackRelease': 'ウォレットの履歴を確認しました — この保留を解除する',
  'swap.origin.trackingFailed':
    'このスワップは記録できなかったため、開始されませんでした。署名は行われず、資金も移動していません。',

  // Earn (lending market; {symbol}, {amount}, {usd}, {digest}, {reason} interpolated)
  'earn.title': '貸す・借りる',
  'earn.subtitle': '保有資産を担保に資金を借りたり、預けて利回りを得たりして、資産を有効活用できます。',
  'earn.error.market': 'マーケットを利用できません：{message}',
  'earn.error.retry': '再試行',
  'earn.loading': 'レンディングマーケットを読み込んでいます…',
  'earn.empty': '現在利用できるレンディングマーケットはありません。',
  'earn.table.asset': '資産',
  'earn.table.totalSupply': '総供給額',
  'earn.table.totalBorrow': '総借入額',
  'earn.table.ltv': 'LTV',
  'earn.table.aria': 'レンディングマーケット',
  'earn.vault.title': '{symbol} Vault',
  'earn.vault.supplyYield': '供給利回り',
  'earn.vault.borrowInterest': '借入金利',
  'earn.vault.marketPrice': '市場価格',
  'earn.vault.utilization': '利用率',
  'earn.vault.depositThreshold': '預入しきい値',
  'earn.vault.borrowThreshold': '借入しきい値',
  'earn.vault.borrowFee': '借入手数料：{pct}%',
  'earn.account.title': 'アカウント',
  'earn.account.deposits': '預入残高',
  'earn.account.borrows': '借入残高',
  'earn.account.borrowLimit': '借入上限',
  'earn.account.liqThreshold': '清算しきい値',
  'earn.account.health': 'アカウント健全性',
  'earn.account.depositedRow': '{amount} {symbol} を預入済み（${usd}）',
  'earn.account.borrowedRow': '{amount} {symbol} を借入済み（${usd}）',
  'earn.action.supply': '預け入れ',
  'earn.action.borrow': '借りる',
  'earn.action.withdraw': '引き出す',
  'earn.action.repay': '返済',
  'earn.actions.aria': 'レンディング操作',
  'earn.amount.label': '金額（{symbol}）',
  'earn.amount.available': '利用可能：約 {amount}',
  'earn.amount.max': '最大',
  'earn.amount.allDebt': '全額返済',
  'earn.submit.submitting': '送信中…',
  'earn.note.sameAssetBorrow': '{symbol} は担保資産のため借りられません。別の資産を選択するか、先に預入を引き出してください。',
  'earn.note.overMax.withdraw': '引き出せるのは約 {amount} {symbol} までです。残りは借入の担保として維持する必要があります。全額引き出すには先に返済してください。',
  'earn.note.overMax.borrow': '担保に基づき、約 {amount} {symbol} まで借りられます。',
  'earn.note.overMax.repay': '返済額は約 {amount} {symbol} です。',
  'earn.note.noSigner': '実行できません。署名はゲートウェイ経由で行われますが、ゲートウェイのレンディングルートにもローカル開発署名機能にも接続できません。',
  'earn.note.devSigner': '開発用署名機能（テストネット）：{address}',
  'earn.toast.confirmed': '{action}が確定しました（{digest}…）',
  'earn.toast.failedReason': '{action}に失敗しました：{reason}',
  'earn.toast.failedOnChain': '{action}に失敗しました：オンチェーンエラー（{digest}…）',
  'earn.activity.title': '最近のアクティビティ',

  // Filters and lists
  'filter.network.aria': 'ネットワークフィルター',
  'filter.chain.aria': 'ブロックチェーンフィルター',
  'filter.chain.selectNetwork': 'ネットワークを選択',
  'holdings.empty': '保有資産はまだありません。資産を受け取るか、フィルターを切り替えてください。',
  'holdings.showHidden': '非表示の {count} 件を表示 · $0.01 未満',
  'holdings.hideSmall': '少額の{word}を {count} 件非表示',
  'holdings.balance': '残高',
  'holdings.balances': '残高',
  'chainSummary.empty': '保有資産はまだありません。資産を受け取るとネットワークが表示されます。',
  'chainSummary.viewAria': '{name} を表示、{count} {word}',
  'walletChain.empty': 'このネットワークにはまだウォレットがありません。',
  'walletChain.openAria': '{label} を開く',

  // Account and wallet management sheets
  'account.closeSwitcher': 'ウォレット切り替えを閉じる',
  'account.title': 'ウォレット',
  'account.allDesc': 'すべてを1つのリストで',
  'account.newWallet': '新しいウォレット',
  'account.searchPlaceholder': '名前またはアドレスで検索',
  'account.searchAria': '名前またはアドレスでウォレットを検索',
  'account.networks': 'ネットワーク',
  'account.empty.filtered': 'フィルターに一致するウォレットがありません。',
  'account.empty.none': 'ウォレットがまだありません。',
  'account.selectAria': '{label}、{value} を選択',
  'account.copyAddressAria': '{chain} アドレスをコピー',
  'account.showQrAria': '{chain} の受取 QR を表示',
  'selectNetwork.closeAria': 'ネットワーク選択を閉じる',
  'selectNetwork.title': 'ネットワークを選択',
  'selectNetwork.searchPlaceholder': 'ネットワークを検索',
  'selectNetwork.searchAria': '名前でネットワークを検索',
  'selectNetwork.groupAria': 'ネットワーク',
  'selectNetwork.all': 'すべてのネットワーク',
  'selectNetwork.empty': '検索に一致するネットワークがありません。',
  'walletTabs.sectionsAria': 'ウォレットのセクション',
  'walletTabs.tokens': 'トークン',
  'walletTabs.approvals': '承認',
  'walletTabs.manage': '暗号資産を管理',
  'walletTabs.history': '履歴',
  'manage.closeAria': '暗号資産管理を閉じる',
  'manage.aria': '{wallet} の暗号資産を管理',
  'manage.title': '暗号資産を管理',
  'manage.searchPlaceholder': 'トークンを検索',
  'manage.searchAria': 'シンボルまたは名前でトークンを検索',
  'manage.empty': 'このウォレットにはまだトークンがありません。資産を受け取るとここで管理できます。',
  'manage.noMatches': '検索に一致するトークンがありません。',
  'manage.tokensAria': 'トークン',
  'manage.showTokenAria': '{network} の {symbol} を表示',
  'manage.import': 'カスタムトークンをインポート',
  'manage.note': 'このウォレットが保有するトークンのみ表示されます。コントラクトアドレスでトークンをインポートすると、カスタム資産を追跡できます。インポートしたトークンは未検証で、価格情報はありません。',
  'import.closeAria': 'トークンのインポートを閉じる',
  'import.aria': '{wallet} にトークンをインポート',
  'import.title': 'トークンをインポート',
  'import.contract': 'コントラクトアドレス',
  'import.contractPlaceholder': 'トークンのコントラクトアドレスを貼り付け',
  'import.contractAria': 'トークンのコントラクトアドレス',
  'import.resolve': 'トークンを確認',
  'import.resolving': '確認中…',
  'import.unknownToken': '不明なトークン',
  'import.unnamedToken': '名前のないトークン',
  'import.decimals': '小数桁数',
  'import.balance': '残高',
  'import.hint': '{network} 上のトークンコントラクトアドレスを貼り付けて確認すると、追加前にプレビューできます。',
  'import.warning': '未検証のトークンです。追加前にコントラクトアドレスを必ずご自身で確認してください。インポートしたトークンは選定済み許可リストに含まれず、価格情報もありません。',
  'import.add': 'トークンを追加',
  'import.error.cap': 'このウォレットはインポート可能なトークン数の上限に達しています。1つ削除してから追加してください。',

  // Wallet balance and wallet settings
  'walletBalance.tag.unverifiedTitle': 'このトークンは選定済み許可リストに含まれていません。',
  'walletBalance.testnetPillTitle': 'テストネットのウォレットです。残高に実際の価値はありません。',
  'walletBalance.testnet': 'テストネット',
  'walletBalance.loadingBalances': '残高を読み込んでいます...',
  'walletBalance.statusPrefix': 'ステータス：{status}',
  'walletBalance.keygen': '鍵生成中です。準備ができると残高が表示されます。',
  'walletBalance.partial': '一部の残高を読み込めなかったため、不完全な可能性があります。',
  'walletBalance.chainFilterAria': 'ネットワークフィルター',
  'walletBalance.testnetBanner': 'テストネット — 表示されている残高はテストネット上のもので、実際の価値はありません。メインネットを使用するには、メニューで開発者モードをオフにしてください。',
  'walletSettings.addressAria': 'ウォレットアドレス',
  'walletSettings.detailsAria': 'ウォレット詳細',
  'walletSettings.address': 'アドレス',
  'walletSettings.details': '詳細',
  'walletSettings.addressPending': 'アドレスを準備中です。ウォレットの鍵が確定すると表示されます。',
  'walletSettings.name': '名前',
  'walletSettings.balance': '残高',
  'walletSettings.status': 'ステータス',
  'walletSettings.curve': '曲線',
  'walletSettings.publicKey': '公開鍵',
  'walletSettings.shortAddress': '短縮アドレス',
  'walletSettings.pending': '保留中',
  'walletSettings.unknownChain': '不明なネットワーク',

  // Welcome / language choice (first-visit screen)
  'welcome.eyebrow': 'ようこそ',
  'welcome.tagline': '大切なものを守る',
  'welcome.title': '言語を選択してください',
  'welcome.cta': 'はじめる',
  'welcome.legalTitle': '法的通知',
  'welcome.legalText': '言語サポートは、世界中のユーザーのアクセシビリティ向上に向けた取り組みの一環として提供されています。このサポートは、その利用が法的に認められている地域でのみ提供され、このサポートを希望または必要とするユーザーの便宜のみを目的としています。',
  'welcome.legalAgreement': '下の「同意する」ボタンをクリックすることで、この言語の使用が現地の法律および規制に準拠していることを確認します。',
  'welcome.agree': '同意する',

  'start.label': 'ウォレット',
  'start.points.badge': 'MPC技術を採用',
  'start.points.lead': '漏えいリスクの高い秘密鍵やシードフレーズは必要ありません。',
  'start.points.shares':
    'ウォレットは、Google、ご自身のバックアップ、デバイス認証に分散したセキュリティ要素（キーシャード）で保護されます。',
  'start.points.twoRequired': '資産を移動するには、少なくとも2つのセキュリティ要素が必要です。',
  'start.points.neverWhole':
    '資産を守るセキュリティ要素は、決して結合されたり、一か所に保存されたりしません。',
  'start.points.rules':
    'セキュリティ要素に問題が生じても、追加のセキュリティルールが資産を保護します。',
  'start.points.more': '続きを読む',
  'start.points.less': '折りたたむ',
  'start.title': '匿名ウォレットを作成',
  'start.desc': 'お使いのデバイスの認証機能で分散型ウォレットを保護します。',
  'start.hint': '顔認証 · 指紋認証 · パスキー',
  'start.signin.prompt': 'すでにアカウントをお持ちですか？',
  'start.signin.link': 'ログイン',
  'start.unsupported': 'このブラウザではパスキーを作成できません。代わりに外部サービスでログインしてください。',

  // Maintenance screen. Shown when the host answers /v1 with 503.
  'maintenance.title': 'ウォレットはメンテナンス中です',
  'maintenance.body': '現在、計画メンテナンスを実施しています。完了後、ウォレットを再びご利用いただけます。',
  'maintenance.assurance': '資産と鍵は安全に保たれています。ログアウトによって保有資産が変わることはありません。',
  'maintenance.retry': 'もう一度確認',
  'start.secondAccount.title': 'このデバイスには、アカウントに紐づく認証方法がすでに登録されています',
  'start.secondAccount.body': '別のアカウントを追加しますか？',
  'start.secondAccount.signIn': 'ログイン画面へ',
  'start.secondAccount.create': '別のアカウントを追加',
};
